import { useState } from "react";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import type { Notification } from "../../types/notifications";
import Styles from "./NotificationPanel.module.scss";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: () => void;
  onMarkAsUnread: () => void;
  onClick: () => void;
  formatTime: (createdAt: string) => string;
}

const NotificationItem = ({
  notification,
  onMarkAsRead,
  onMarkAsUnread,
  onClick,
  formatTime,
}: NotificationItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const isUnread = notification.status === "unread";

  const handleActionClick = () => {
    if (isUnread) {
      onMarkAsRead();
    } else {
      onMarkAsUnread();
    }
    // Close dropdown only after action is performed
    setOpen(false);
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'mark',
      label: isUnread ? 'Mark as read' : 'Mark as unread',
      onClick: handleActionClick,
    },
  ];

  const handleOpenChange = (newOpen: boolean) => {
    // Allow opening and closing - dropdown stays open until user performs action or clicks outside/dot again
    setOpen(newOpen);
  };

  const handleNotificationRowClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the status indicator or dropdown
    const target = e.target as HTMLElement;
    if (target.closest(`.${Styles.statusIndicatorWrapper}`) || 
        target.closest('.ant-dropdown')) {
      return;
    }
    
    // Close dropdown if open when clicking elsewhere
    if (open) {
      setOpen(false);
      return;
    }
    
    // Navigate on row click
    onClick();
  };

  // Parse description to handle bold text
  const renderDescription = (description: string) => {
    const parts = description.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        const text = part.slice(2, -2);
        return (
          <strong key={index}>{text}</strong>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div
      className={`${Styles.notificationItem} ${isUnread ? Styles.unread : Styles.read}`}
      onClick={handleNotificationRowClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={Styles.notificationContent}>
        <div className={Styles.statusIndicatorWrapper}>
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            open={open}
            onOpenChange={handleOpenChange}
            placement="bottomLeft"
            overlayClassName={Styles.notificationDropdown}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
            destroyPopupOnHide={false}
          >
            <div
              className={`${Styles.statusIndicator} ${isUnread ? Styles.unreadIndicator : Styles.readIndicator}`}
              onClick={(e) => e.stopPropagation()}
              title={isUnread ? "Mark as read" : "Mark as unread"}
            />
          </Dropdown>
        </div>
        <div className={Styles.notificationText}>
          <div className={Styles.notificationTitle}>{notification.title}</div>
          <div className={Styles.notificationDescription}>
            {renderDescription(notification.description)}
          </div>
        </div>
        <div className={Styles.notificationTime}>
          {formatTime(notification.createdAt)}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;

